import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Check, Package, Ruler, Gem, Tag } from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const product = products.find((p) => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-textSecondary mb-4">Produit introuvable.</p>
          <button onClick={() => navigate('/boutique')} className="btn-primary">
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/boutique')}
          className="flex items-center gap-2 text-textSecondary hover:text-primary-500 transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Retour à la boutique
        </button>

        {/* Product */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Image */}
            <div className="relative h-80 lg:h-auto min-h-[400px] overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {!product.available && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="bg-white text-textPrimary text-lg font-semibold px-6 py-3 rounded-full">
                    Épuisé
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-primary-50 text-primary-500 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {product.category}
                </span>
                {product.available ? (
                  <span className="bg-green-50 text-green-600 text-xs font-medium px-3 py-1 rounded-full">
                    En stock
                  </span>
                ) : (
                  <span className="bg-red-50 text-red-500 text-xs font-medium px-3 py-1 rounded-full">
                    Épuisé
                  </span>
                )}
              </div>

              <h1 className="text-3xl font-display font-bold text-textPrimary mb-3">
                {product.name}
              </h1>
              <p className="text-textSecondary mb-6">{product.fullDescription}</p>

              {/* Details */}
              <div className="space-y-3 mb-8 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3 text-sm">
                  <Gem className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <span className="text-textSecondary">Matière :</span>
                  <span className="font-medium text-textPrimary">{product.material}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Ruler className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <span className="text-textSecondary">Taille :</span>
                  <span className="font-medium text-textPrimary">{product.size}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Package className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <span className="text-textSecondary">Disponibilité :</span>
                  <span className={`font-medium ${product.available ? 'text-green-600' : 'text-red-500'}`}>
                    {product.available ? 'En stock' : 'Épuisé'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-4xl font-bold text-primary-400">
                  {product.price.toFixed(2)} €
                </span>
                <button
                  onClick={handleAddToCart}
                  disabled={!product.available}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    !product.available
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : added
                      ? 'bg-green-500 text-white shadow-lg'
                      : 'bg-primary-300 hover:bg-primary-400 text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                  }`}
                >
                  {added ? (
                    <>
                      <Check className="w-5 h-5" />
                      Ajouté !
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Ajouter au panier
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-2xl font-display font-semibold text-textPrimary mb-6">
              Vous aimerez aussi
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group transform hover:-translate-y-1"
                  onClick={() => navigate(`/boutique/${p.id}`)}
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-textPrimary text-sm">{p.name}</h3>
                      <span className="text-primary-400 font-bold">{p.price.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
